// Utility functions for RSS Feed features

/**
 * Format date to relative time (e.g., "2 hours ago", "Yesterday")
 */
export const formatRelativeTime = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now - date;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} week${weeks > 1 ? 's' : ''} ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} month${months > 1 ? 's' : ''} ago`;
  }
  return date.toLocaleDateString();
};

/**
 * Group items by date
 */
export const groupByDate = (items) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  
  const groups = {
    'Today': [],
    'Yesterday': [],
    'This Week': [],
    'Older': []
  };
  
  items.forEach(item => {
    const itemDate = new Date(item.pubDate);
    itemDate.setHours(0, 0, 0, 0);
    const diffMs = today - itemDate;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      groups['Today'].push(item);
    } else if (diffDays === 1) {
      groups['Yesterday'].push(item);
    } else if (diffDays <= 7) {
      groups['This Week'].push(item);
    } else {
      groups['Older'].push(item);
    }
  });
  
  return groups;
};

/**
 * Strip HTML tags from text and remove image tags to prevent loading errors
 */
export const stripHtmlTags = (html) => {
  if (!html) return '';
  
  // Remove image tags and YouTube embeds before parsing to prevent 404 errors
  let cleanedHtml = html
    // Remove all img tags (prevents YouTube thumbnail loading errors)
    .replace(/<img[^>]*>/gi, '')
    // Remove YouTube iframe embeds
    .replace(/<iframe[^>]*youtube[^>]*>.*?<\/iframe>/gi, '')
    // Remove YouTube video links that might cause image loading
    .replace(/https?:\/\/img\.youtube\.com\/[^\s<>"]+/gi, '')
    // Remove other common image URLs that might fail
    .replace(/https?:\/\/[^\s<>"]+\.(jpg|jpeg|png|gif|webp|svg)/gi, '')
    // Remove script tags
    .replace(/<script[^>]*>.*?<\/script>/gi, '')
    // Remove style tags
    .replace(/<style[^>]*>.*?<\/style>/gi, '');
  
  // Create a temporary div element to parse HTML
  const tmp = document.createElement('div');
  tmp.innerHTML = cleanedHtml;
  
  // Get text content and clean up whitespace
  const text = tmp.textContent || tmp.innerText || '';
  return text.replace(/\s+/g, ' ').trim();
};

/**
 * Format a date as a short absolute string, e.g. "Jan 2025"
 */
export const formatAbsoluteDate = (dateString) => {
  const date = new Date(dateString);
  if (isNaN(date)) return null;
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

/**
 * Truncate text to specified length (strips HTML first)
 */
export const truncateText = (text, maxLength = 150) => {
  if (!text) return '';
  // Strip HTML tags first
  const cleanText = stripHtmlTags(text);
  if (cleanText.length <= maxLength) return cleanText;
  return cleanText.substring(0, maxLength).trim() + '...';
};

